/**
 * PROCEDURAL BACKGROUND PATTERNS — generates seamlessly-tiling SVG motifs from a small slider-driven param
 * set, encoded as a data: URI for use as a CSS background-image. Because it's SVG it can draw intricate gothic
 * tracery and geometric lattices that flat CSS gradients can't, while still tiling and staying resolution-free.
 *
 * The site theme stores a PatternParams object; siteTheme.applySiteTheme() turns it into `--pat-image`. The
 * admin theme editor exposes the params as sliders (with per-family labels) plus a gallery of PRESETS that just
 * load a params object into the builder to fine-tune.
 *
 * Every generator draws into a `tile × tile` viewBox and only uses strokes/fills of `color`, so the motif edges
 * meet cleanly across tile borders (drawn symmetric about the tile) → the repeat reads as continuous.
 */

export type MotifFamily = "gothic" | "geometric" | "linework" | "damask";

export interface PatternParams {
  family: MotifFamily;
  /** 0..1 normalized sliders — meaning depends on family (see SLIDER_LABELS). */
  a: number;   // primary count (foils / star-points / sides / frequency)
  b: number;   // secondary shape (cusp depth / inner radius / amplitude / motif scale)
  c: number;   // stroke weight
  d: number;   // rotation
  e: number;   // density / spacing
  fill: boolean;   // filled motif vs. outline-only
}

export const DEFAULT_PATTERN_PARAMS: PatternParams = {
  family: "gothic", a: 0.5, b: 0.5, c: 0.35, d: 0, e: 0.5, fill: false,
};

/** Per-family slider labels, so the UI relabels the same 5 sliders as you switch motif family. */
export const SLIDER_LABELS: Record<MotifFamily, { a: string; b: string; c: string; d: string; e: string }> = {
  gothic:    { a: "Foils",      b: "Cusp depth",   c: "Stroke",  d: "Rotation", e: "Density" },
  geometric: { a: "Points/Sides", b: "Inner radius", c: "Stroke", d: "Rotation", e: "Density" },
  linework:  { a: "Frequency",  b: "Amplitude",    c: "Stroke",  d: "Angle",    e: "Spacing" },
  damask:    { a: "Motif",      b: "Scale",        c: "Stroke",  d: "Rotation", e: "Spacing" },
};

const TAU = Math.PI * 2;
const r2 = (n: number) => Math.round(n * 100) / 100;

/** Map a normalized 0..1 slider onto an integer range [lo,hi]. */
function iRange(v: number, lo: number, hi: number): number { return Math.round(lo + (hi - lo) * clamp01(v)); }
function fRange(v: number, lo: number, hi: number): number { return lo + (hi - lo) * clamp01(v); }
function clamp01(v: number): number { return Math.max(0, Math.min(1, v)); }

/** Build the SVG string (not yet URI-encoded) for the given params + tile size + color. */
export function patternSvg(p: PatternParams, tile: number, color: string): string {
  const sw = r2(fRange(p.c, 0.4, 3.2));   // stroke width shared by families
  let body = "";
  switch (p.family) {
    case "gothic":    body = gothic(p, tile, color, sw); break;
    case "geometric": body = geometric(p, tile, color, sw); break;
    case "linework":  body = linework(p, tile, color, sw); break;
    case "damask":    body = damask(p, tile, color, sw); break;
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${tile}" height="${tile}" viewBox="0 0 ${tile} ${tile}">${body}</svg>`;
}

/** Full CSS background-image value: url(data:...) for the given params. */
export function patternImageCss(p: PatternParams, tile: number, color: string): string {
  const svg = patternSvg(p, tile, color);
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

// ─────────────────────────────────────────────────────────────────────────────
// GOTHIC — foils (trefoil/quatrefoil/…) inside a ring, with pointed cusps, plus corner quarter-motifs so the
// lattice reads continuously across tiles.
function gothic(p: PatternParams, tile: number, color: string, sw: number): string {
  const foils = iRange(p.a, 3, 8);               // 3=trefoil … 8=octofoil
  const cusp = fRange(p.b, 0.12, 0.42);          // how deep the cusps bite
  const rot = fRange(p.d, 0, TAU);
  const R = tile * fRange(p.e, 0.28, 0.46);      // motif radius (density = smaller/denser when low)
  const cx = tile / 2, cy = tile / 2;
  const stroke = p.fill ? `fill="${color}" fill-opacity="0.9"` : `fill="none" stroke="${color}" stroke-width="${sw}"`;
  const centre = foilPath(cx, cy, R, foils, cusp, rot);
  // Quarter motifs at the four corners → the ring meets its neighbours across the tile seam.
  const corners = corners4(tile)
    .map(([x, y]) => `<path d="${foilPath(x, y, R, foils, cusp, rot)}" fill="none" stroke="${color}" stroke-width="${sw}"/>`)
    .join("");
  const ring = `<circle cx="${cx}" cy="${cy}" r="${r2(R * 0.5)}" fill="none" stroke="${color}" stroke-width="${r2(sw * 0.7)}"/>`;
  return `<path d="${centre}" ${stroke}/>${ring}${corners}`;
}

/** A closed foil (multi-lobe) path — lobes are semicircle bumps around a base circle with cusped valleys. */
function foilPath(cx: number, cy: number, R: number, foils: number, cusp: number, rot: number): string {
  const pts: string[] = [];
  const steps = foils * 24;
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * TAU;
    // radius modulated by a raised cosine → rounded lobes with pinched cusps between them
    const lobe = Math.abs(Math.cos((foils * (t)) / 2));
    const rr = R * (1 - cusp + cusp * Math.pow(lobe, 1.6));
    const x = cx + Math.cos(t + rot) * rr;
    const y = cy + Math.sin(t + rot) * rr;
    pts.push(`${r2(x)},${r2(y)}`);
  }
  return `M${pts.join(" L")}Z`;
}

// ─────────────────────────────────────────────────────────────────────────────
// GEOMETRIC — an N-point star OR N-gon at the tile centre + corners, optional interlace feel via a second
// rotated copy. Reads as a tessellated lattice when dense.
function geometric(p: PatternParams, tile: number, color: string, sw: number): string {
  const pts = iRange(p.a, 3, 12);                 // star points / polygon sides
  const inner = fRange(p.b, 0.32, 0.86);          // inner radius ratio (low = spiky star, high = polygon-ish)
  const rot = fRange(p.d, 0, TAU / pts);
  const R = tile * fRange(p.e, 0.26, 0.48);
  const stroke = p.fill ? `fill="${color}" fill-opacity="0.85"` : `fill="none" stroke="${color}" stroke-width="${sw}" stroke-linejoin="round"`;
  const star = (cx: number, cy: number, extraRot: number) => {
    const arr: string[] = [];
    const n = pts * 2;
    for (let i = 0; i < n; i++) {
      const ang = (i / n) * TAU + rot + extraRot;
      const rr = i % 2 === 0 ? R : R * inner;
      arr.push(`${r2(cx + Math.cos(ang) * rr)},${r2(cy + Math.sin(ang) * rr)}`);
    }
    return `M${arr.join(" L")}Z`;
  };
  const cx = tile / 2, cy = tile / 2;
  const centre = `<path d="${star(cx, cy, 0)}" ${stroke}/>`;
  // A faint second copy rotated a half-step gives an interlace/girih hint.
  const second = `<path d="${star(cx, cy, TAU / (pts * 2))}" fill="none" stroke="${color}" stroke-width="${r2(sw * 0.6)}" stroke-opacity="0.6"/>`;
  const corners = corners4(tile)
    .map(([x, y]) => `<path d="${star(x, y, 0)}" fill="none" stroke="${color}" stroke-width="${sw}"/>`).join("");
  return `${centre}${second}${corners}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// LINEWORK — guilloché-style sine weave: a set of horizontal sine bands (rotatable), phase-shifted, that tile
// vertically. Angle rotates the whole field.
function linework(p: PatternParams, tile: number, color: string, sw: number): string {
  const freq = iRange(p.a, 1, 6);                 // full sine cycles across the tile
  const amp = tile * fRange(p.b, 0.03, 0.16);
  const angle = r2(fRange(p.d, 0, 180));
  const rows = iRange(p.e, 2, 10);                // number of woven bands
  const paths: string[] = [];
  for (let row = 0; row < rows; row++) {
    const baseY = ((row + 0.5) / rows) * tile;
    const phase = (row / rows) * TAU;
    const seg: string[] = [];
    const steps = 48;
    for (let i = 0; i <= steps; i++) {
      const x = (i / steps) * tile;
      const y = baseY + Math.sin((x / tile) * TAU * freq + phase) * amp;
      seg.push(`${r2(x)},${r2(y)}`);
    }
    paths.push(`<path d="M${seg.join(" L")}" fill="none" stroke="${color}" stroke-width="${sw}"/>`);
  }
  return `<g transform="rotate(${angle} ${tile / 2} ${tile / 2})">${paths.join("")}</g>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// DAMASK — a small ornamental motif powdered on a diamond (drop-repeat) diaper. Motif index picks fleur-de-lis /
// diamond / sprig; scale + spacing set the diaper.
function damask(p: PatternParams, tile: number, color: string, sw: number): string {
  const motif = iRange(p.a, 0, 2);                // 0 fleur-de-lis, 1 diamond, 2 sprig(quatrefoil-dot)
  const scale = fRange(p.b, 0.5, 1.15);
  const rot = fRange(p.d, 0, TAU);
  const stroke = p.fill ? `fill="${color}" fill-opacity="0.85" stroke="none"` : `fill="none" stroke="${color}" stroke-width="${sw}"`;
  // Two lattice points (centre + a dropped corner set) give the classic offset diaper.
  const s = tile * 0.28 * scale;
  const one = (cx: number, cy: number) =>
    `<g transform="translate(${r2(cx)} ${r2(cy)}) rotate(${r2((rot * 180) / Math.PI)}) scale(${r2(scale)})" ${stroke}>${motifPath(motif, s)}</g>`;
  const half = tile / 2;
  return [one(half, half), one(0, 0), one(tile, 0), one(0, tile), one(tile, tile)].join("");
}

function motifPath(motif: number, s: number): string {
  if (motif === 0) {
    // Fleur-de-lis: a central lance + two curling side lobes + a band.
    const u = s * 0.5;
    return `<path d="M0,${-u} C ${u * 0.55},${-u * 0.2} ${u * 0.35},${u * 0.5} 0,${u} C ${-u * 0.35},${u * 0.5} ${-u * 0.55},${-u * 0.2} 0,${-u} Z"/>` +
      `<path d="M0,${u * 0.1} C ${u},${-u * 0.1} ${u * 0.8},${u * 0.7} ${u * 0.15},${u * 0.6}"/>` +
      `<path d="M0,${u * 0.1} C ${-u},${-u * 0.1} ${-u * 0.8},${u * 0.7} ${-u * 0.15},${u * 0.6}"/>` +
      `<rect x="${-u * 0.5}" y="${u * 0.55}" width="${u}" height="${u * 0.18}"/>`;
  }
  if (motif === 1) {
    // Diamond diaper with an inset dot.
    const u = s * 0.5;
    return `<path d="M0,${-u} L${u},0 L0,${u} L${-u},0 Z"/><circle cx="0" cy="0" r="${r2(u * 0.22)}"/>`;
  }
  // sprig: a four-dot quatrefoil cluster.
  const u = s * 0.34;
  const dots: Array<[number, number]> = [[0, -u], [u, 0], [0, u], [-u, 0]];
  return dots.map(([x, y]) => `<circle cx="${r2(x)}" cy="${r2(y)}" r="${r2(u * 0.5)}"/>`).join("") +
    `<circle cx="0" cy="0" r="${r2(u * 0.3)}"/>`;
}

/** The four tile corners as typed [x,y] tuples (so array-destructure stays number, not number|undefined). */
function corners4(tile: number): Array<[number, number]> {
  return [[0, 0], [tile, 0], [0, tile], [tile, tile]];
}

/** Named presets — each just seeds the builder params (the gallery "load into builder, then tweak" flow). */
export interface PatternPreset { id: string; label: string; params: PatternParams; tile: number; }
export const PATTERN_PRESETS: PatternPreset[] = [
  { id: "quatrefoil", label: "Quatrefoil", tile: 200, params: { family: "gothic", a: 0.3, b: 0.5, c: 0.35, d: 0, e: 0.5, fill: false } },
  { id: "trefoil",    label: "Trefoil",    tile: 180, params: { family: "gothic", a: 0.0, b: 0.55, c: 0.4, d: 0.16, e: 0.42, fill: false } },
  { id: "octofoil",   label: "Rose window",tile: 240, params: { family: "gothic", a: 1.0, b: 0.34, c: 0.3, d: 0, e: 0.6, fill: false } },
  { id: "star8",      label: "8-star",     tile: 200, params: { family: "geometric", a: 0.5, b: 0.42, c: 0.4, d: 0, e: 0.5, fill: false } },
  { id: "star6",      label: "6-star",     tile: 180, params: { family: "geometric", a: 0.33, b: 0.5, c: 0.4, d: 0, e: 0.5, fill: false } },
  { id: "girih",      label: "Girih",      tile: 220, params: { family: "geometric", a: 0.55, b: 0.7, c: 0.3, d: 0.2, e: 0.6, fill: false } },
  { id: "hexlattice", label: "Hex lattice",tile: 160, params: { family: "geometric", a: 0.33, b: 0.86, c: 0.35, d: 0, e: 0.7, fill: false } },
  { id: "guilloche",  label: "Guilloché",  tile: 200, params: { family: "linework", a: 0.6, b: 0.5, c: 0.35, d: 0, e: 0.6, fill: false } },
  { id: "chevron",    label: "Chevron",    tile: 140, params: { family: "linework", a: 0.4, b: 0.9, c: 0.4, d: 0, e: 0.5, fill: false } },
  { id: "moire",      label: "Moiré weave",tile: 240, params: { family: "linework", a: 1.0, b: 0.3, c: 0.25, d: 0.25, e: 0.9, fill: false } },
  { id: "fleur",      label: "Fleur-de-lis",tile: 180, params: { family: "damask", a: 0.0, b: 0.6, c: 0.35, d: 0, e: 0.5, fill: false } },
  { id: "diaper",     label: "Diamond diaper",tile: 140, params: { family: "damask", a: 0.5, b: 0.7, c: 0.4, d: 0, e: 0.5, fill: false } },
  { id: "damask",     label: "Damask sprig",tile: 200, params: { family: "damask", a: 1.0, b: 0.8, c: 0.35, d: 0.2, e: 0.6, fill: true } },
];
